import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { acortarTexto } from '../../helpers/textFormatHelpers.js'
import { useImagenesPublicacion } from '../../hooks/useImagenesPublicacion.js'
import {
  calcularTotalItemSinIva,
  obtenerPrecioUnitarioMayorista,
  toNumber
} from '../helpers/calcularPrecioTotal'
import Imagen from '../imagen/Imagen'
import Precios from '../reutilizables/precios/Precios'
import { DetalleImpuestos } from '../ventas/DetalleImpuestos'
import styles from './DetallePrecio.module.css'

const IVA_ENVIO = 1.21

export const DetallePrecio = ({
  titulo,
  pedidoItems,
  valorUnitarioSinIva,
  esMayorista,
  costoEnvio = 0,
  usaImagen = false,
  pedidoCompletoEsCtaCte = false,
  percepciones = [],
  esVentas = false,
  mensajeAdicional = null,
  impuestosConfig = {},
  costoEnvioConfig = {}
}) => {
  const { onChangeCostoEnvio, errorCostoEnvio = null, correspondeEnvioCosto } = costoEnvioConfig
  const { filasImpuestos, onChangeFilasImpuestos, erroresImpuestos, setErroresImpuestos } =
    impuestosConfig || {}
  const { adjuntos, loadingImagenes, fetchImagenes } = useImagenesPublicacion()
  const [costoEnvioInput, setCostoEnvioInput] = useState(costoEnvio.toString())

  useEffect(() => {
    setCostoEnvioInput(costoEnvio.toString())
  }, [costoEnvio])

  const costoEnvioActual = useMemo(() => {
    if (!esVentas) {
      return Number(costoEnvio) || 0
    }

    const valor = Number(costoEnvioInput)
    return Number.isFinite(valor) && valor >= 0 ? valor : 0
  }, [esVentas, costoEnvio, costoEnvioInput])

  const envioBruto = costoEnvioActual * IVA_ENVIO

  const handleCostoEnvioChange = (e) => {
    const normalizado = e.target.value.replace(',', '.')
    if (/^\d*(\.\d{0,2})?$/.test(normalizado) || normalizado === '') {
      setCostoEnvioInput(normalizado)
      onChangeCostoEnvio?.(normalizado === '' ? 0 : Number(normalizado))
    }
  }

  const calcularNetoItem = (item) => {
    const precioUnitario = obtenerPrecioUnitarioMayorista(item) || valorUnitarioSinIva(item) || 0
    const porcDcto = toNumber(item.porcDcto)
    const factorDescuento = porcDcto > 0 && porcDcto < 100 ? 1 - porcDcto / 100 : 1
    const cantidad = toNumber(item.cantidad) || 1
    return precioUnitario * factorDescuento * cantidad
  }

  const calcularTotalConIvaItem = (item) => {
    const alicuota = toNumber(item.alicuota)
    return calcularNetoItem(item) * (1 + alicuota / 100)
  }

  const netoTotal = useMemo(() => {
    const netoItems = (pedidoItems ?? []).reduce((acc, item) => {
      return acc + calcularNetoItem(item)
    }, 0)

    return netoItems + costoEnvioActual
  }, [pedidoItems, costoEnvioActual])

  const ivaTotalCompra = useMemo(() => {
    const netoItems = (pedidoItems ?? []).reduce((acc, item) => {
      return acc + calcularNetoItem(item)
    }, 0)

    const totalConIvaItems = (pedidoItems ?? []).reduce((acc, item) => {
      return acc + calcularTotalConIvaItem(item)
    }, 0)

    const ivaItems = Math.max(0, totalConIvaItems - netoItems)
    const ivaEnvio = costoEnvioActual > 0 ? envioBruto - costoEnvioActual : 0

    return Math.max(0, ivaItems + ivaEnvio)
  }, [pedidoItems, costoEnvioActual, envioBruto])

  const adaptarPercepciones = () => {
    return percepciones.map((p) => ({
      ...p,
      existente: true,
      pendienteEliminar: false,
      idFila: `p_${p.idProvincia}`
    }))
  }

  const totalPercepciones = useMemo(() => {
    const filas = esVentas ? filasImpuestos : percepciones
    return (filas ?? [])
      .filter((f) => !f.pendienteEliminar)
      .reduce((acc, f) => acc + (Number(f.monto) || 0), 0)
  }, [esVentas, filasImpuestos, percepciones])

  useEffect(() => {
    if (pedidoItems.length > 0) {
      fetchImagenes({
        idPublicaciones: pedidoItems.map((p) => p.idPublicacion)
      })
    }
  }, [pedidoItems])

  return (
    <div className={styles.contenedor}>
      {titulo && <h3 className={styles.titulo}>{titulo}</h3>}
      <div className={styles.header}>
        {usaImagen && <div className={`${styles.headerItem} ${styles.colImagen}`}></div>}
        <div className={`${styles.headerItem} ${styles.colTitulo}`}>Item</div>
        <div className={`${styles.headerItem} ${styles.colCantidad}`}>Cantidad</div>
        <div className={`${styles.headerItem} ${styles.colPrecio}`}>Precio</div>
      </div>
      {pedidoItems.map((item) => (
        <div
          key={`${item.idPedidoItem || item.idPresupuestoItem || item.idPublicacion}`}
          className={styles.itemRow}
        >
          {usaImagen && (
            <div className={styles.itemImagen}>
              <Imagen
                adjunto={adjuntos.find((a) => a.idPublicacion === item.idPublicacion)}
                loadingImagenes={loadingImagenes}
                width={60}
                height={60}
              />
            </div>
          )}
          <div className={styles.itemTitulo}>
            {item.idPresupuestoItem > 0 || item.idPresupuesto > 0
              ? acortarTexto(item.descripcion)
              : acortarTexto(item.titulo)}
          </div>
          <div className={styles.itemCantidad}>{item.cantidad}</div>
          <div className={styles.itemPrecio}>
            <Precios
              precio={obtenerPrecioUnitarioMayorista(item)}
              monedaSimbolo={item.monedaSimbolo}
              alicuota={item.alicuota}
              cantidad={1}
              showSinIVA={true}
              sufijo={esMayorista(item) ? '(mayorista)' : ''}
              porcDcto={item.porcDcto}
              className={styles.precioAlineado}
            />
          </div>
        </div>
      ))}
      {costoEnvioActual > 0 || (esVentas && correspondeEnvioCosto) ? (
        <div className={styles.itemRow}>
          {usaImagen && <div className={styles.itemImagen}></div>}
          <div className={styles.itemTitulo}>
            <strong>Costo de Envío</strong>
          </div>
          <div className={styles.itemCantidad}></div>
          <div className={styles.itemPrecio}>
            {esVentas && correspondeEnvioCosto ? (
              <div>
                <input
                  type="text"
                  inputMode="decimal"
                  value={costoEnvioInput}
                  onChange={handleCostoEnvioChange}
                  className={styles.inputEnvio}
                  placeholder="0.00"
                  aria-label="Costo de envío"
                />
                {errorCostoEnvio && <small className={styles.errorInline}>{errorCostoEnvio}</small>}
              </div>
            ) : (
              <Precios
                nombre="Envío"
                precio={costoEnvioActual}
                monedaSimbolo="$"
                alicuota={21}
                showSinIVA={true}
                className={styles.precioAlineado}
              />
            )}
          </div>
        </div>
      ) : null}
      <div className={styles.totalRow}>
        {usaImagen && <div className={styles.colImagen}></div>}
        <div className={styles.totalLabel}>Total Neto</div>
        <div className={styles.colCantidad}></div>
        <div className={styles.totalValue}>
          <Precios
            nombre=""
            precio={netoTotal}
            monedaSimbolo="$"
            alicuota={0}
            cantidad={1}
            showSinIVA={false}
            className={styles.precioAlineado}
          />
        </div>
      </div>
      <div className={styles.totalRow}>
        {usaImagen && <div className={styles.colImagen}></div>}
        <div className={styles.totalLabel}>IVA</div>
        <div className={styles.colCantidad}></div>
        <div className={styles.totalValue}>
          <Precios
            nombre=""
            precio={ivaTotalCompra}
            monedaSimbolo="$"
            alicuota={0}
            cantidad={1}
            showSinIVA={false}
            className={styles.precioAlineado}
          />
        </div>
      </div>
      {(esVentas || percepciones?.length > 0) && !pedidoCompletoEsCtaCte && (
        <DetalleImpuestos
          esVentas={esVentas}
          filas={esVentas ? filasImpuestos : adaptarPercepciones()}
          onChangeFilas={onChangeFilasImpuestos}
          errores={erroresImpuestos}
          setErrores={setErroresImpuestos}
          usaImagen={usaImagen}
          pedidoItems={pedidoItems}
          obtenerNetoItem={(item) => calcularTotalItemSinIva(item)}
        />
      )}
      {pedidoItems?.[0]?.anticipo > 0 && (
        <div className={styles.totalFinalRow}>
          {usaImagen && <div className={styles.colImagen}></div>}
          <div className={styles.totalFinalLabel}>Anticipo</div>
          <div className={styles.colCantidad}></div>
          <div className={styles.totalFinalValue}>
            <Precios
              items={pedidoItems}
              showSinIVA={false}
              monedaSimbolo="$"
              costoEnvio={costoEnvioActual}
              impuestos={!pedidoCompletoEsCtaCte ? totalPercepciones : 0}
              anticipo={pedidoItems[0].anticipo}
              className={styles.precioAlineado}
            />
          </div>
        </div>
      )}
      <div className={styles.totalFinalRow}>
        {usaImagen && <div className={styles.colImagen}></div>}
        <div className={styles.totalFinalLabel}>Total</div>
        <div className={styles.colCantidad}></div>
        <div className={styles.totalFinalValue}>
          <Precios
            items={pedidoItems}
            showSinIVA={false}
            monedaSimbolo="$"
            costoEnvio={costoEnvioActual}
            impuestos={!pedidoCompletoEsCtaCte ? totalPercepciones : 0}
            className={styles.precioAlineado}
          />
        </div>
      </div>
      {(pedidoCompletoEsCtaCte || mensajeAdicional) && (
        <div className={styles.mensajeCtaCte} style={{ gap: '1rem' }}>
          {pedidoCompletoEsCtaCte && (
            <div className={styles.mensajeBox}>
              <strong>Importante:</strong> Las percepciones propuestas serán verificadas entre
              facturación del vendedor y cuentas a pagar del comprador al momento del pago.
            </div>
          )}
          {mensajeAdicional && <div className={`${styles.mensajeBox}`}>{mensajeAdicional}</div>}
        </div>
      )}
    </div>
  )
}

DetallePrecio.propTypes = {
  titulo: PropTypes.string,
  pedidoItems: PropTypes.arrayOf(
    PropTypes.shape({
      idPublicacion: PropTypes.number.isRequired,
      titulo: PropTypes.string,
      cantidad: PropTypes.number.isRequired,
      monedaSimbolo: PropTypes.string,
      alicuota: PropTypes.number,
      precioUnitario: PropTypes.number,
      valorTotalConIva: PropTypes.number,
      preciosMayoristas: PropTypes.arrayOf(
        PropTypes.shape({
          cantidad: PropTypes.number,
          precioUnitario: PropTypes.number
        })
      ),
      porcDcto: PropTypes.number,
      anticipo: PropTypes.number
    })
  ).isRequired,
  valorUnitarioSinIva: PropTypes.func.isRequired,
  esMayorista: PropTypes.func.isRequired,
  costoEnvio: PropTypes.number,
  usaImagen: PropTypes.bool.isRequired,
  porcDcto: PropTypes.number,
  pedidoCompletoEsCtaCte: PropTypes.bool,
  mensajeAdicional: PropTypes.node,
  percepciones: PropTypes.arrayOf(
    PropTypes.shape({
      idProvincia: PropTypes.number.isRequired,
      nombreProvincia: PropTypes.string,
      alicuota: PropTypes.number.isRequired,
      baseImponible: PropTypes.number.isRequired,
      monto: PropTypes.number.isRequired,
      tipoImpuesto: PropTypes.string
    })
  ),
  esVentas: PropTypes.bool,
  impuestosConfig: PropTypes.shape({
    filasImpuestos: PropTypes.arrayOf(
      PropTypes.shape({
        idFila: PropTypes.string,
        idProvincia: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        nombreProvincia: PropTypes.string,
        alicuota: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        tipoImpuesto: PropTypes.string,
        baseImponible: PropTypes.number,
        monto: PropTypes.number,
        existente: PropTypes.bool,
        pendienteEliminar: PropTypes.bool
      })
    ),
    onChangeFilasImpuestos: PropTypes.func,
    erroresImpuestos: PropTypes.objectOf(PropTypes.string),
    setErroresImpuestos: PropTypes.func
  }),
  costoEnvioConfig: PropTypes.shape({
    onChangeCostoEnvio: PropTypes.func,
    errorCostoEnvio: PropTypes.string,
    correspondeEnvioCosto: PropTypes.bool
  })
}
